import React, { useEffect, useState } from 'react';
import { Card, Button, Typography, Alert, Space, Spin } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api  from '../../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

interface SubscriptionStatusProps {
  onUpgradeClick?: () => void;
  onManageClick?: () => void;
}

export const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  onUpgradeClick,
  onManageClick,
}) => {
  const [status, setStatus] = useState<{
    hasActiveSubscription: boolean;
    planName?: string;
    expiresAt?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        const response = await api.get('/teachers/subscription/status');
        setStatus(response.data);
      } catch (err) {
        console.error('Error fetching subscription status:', err);
        setError('Failed to load subscription status');
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spin />
        <p>Loading subscription status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  return (
    <Card className="mb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <Title level={4} className="mb-2">
            Subscription Status
          </Title>
          {status?.hasActiveSubscription ? (
            <div>
              <div className="flex items-center mb-2">
                <CheckCircleOutlined className="text-green-500 text-xl mr-2" />
                <Text strong>Active Plan: {status.planName}</Text>
              </div>
              {status.expiresAt && (
                <div className="flex items-center text-gray-600">
                  <ClockCircleOutlined className="mr-2" />
                  <Text>
                    Renews {dayjs(status.expiresAt).fromNow()} on{' '}
                    {dayjs(status.expiresAt).format('MMM D, YYYY')}
                  </Text>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center">
              <CloseCircleOutlined className="text-red-500 text-xl mr-2" />
              <Text>No active subscription</Text>
            </div>
          )}
        </div>
        <Space className="mt-4 md:mt-0">
          {status?.hasActiveSubscription ? (
            <Button type="default" onClick={onManageClick}>
              Manage Subscription
            </Button>
          ) : (
            <Button type="primary" onClick={onUpgradeClick}>
              Upgrade Now
            </Button>
          )}
        </Space>
      </div>
    </Card>
  );
};

export default SubscriptionStatus;
